import { Suspense } from "react"
import { useTranslation } from "react-i18next"
import { t } from "i18next"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContentCustom, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { fetchChPatient } from "@/lib/fetch-ch-patient"
import { CareProviderSearch } from "@/modules/care-provider/simple-search/care-provider-search"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { chPatientByIdQuery$data } from "@/modules/patients/services/ch/__generated__/chPatientByIdQuery.graphql"
import { useChCreateDoctorNetworkMutation } from "@/modules/patients/services/ch/chCreateDoctorNetworkMutation"

type Props = {
  open?: boolean
  toggleDialog?: () => void
  doctors?: NonNullable<chPatientByIdQuery$data["patientById"]>["doctorNetworks"]
}

const AddDoctorNetworkDialog = ({ open, doctors, toggleDialog }: Props) => {
  const { i18n } = useTranslation()
  const patientId = usePatientParam()

  const [commit] = useChCreateDoctorNetworkMutation()

  const handleSelect = (careProviderId: string) => {
    toggleDialog?.()
    const exists = doctors?.some((item) => item.careProvider.id === careProviderId)

    if (exists) return
    commit({
      variables: {
        input: { careProviderId, patientId },
      },
      onCompleted: () => {
        fetchChPatient({ patientId, language: i18n.language })
        setTimeout(() => {
          document.getElementById(careProviderId)?.focus()
        }, 300)
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={toggleDialog}>
      <DialogContentCustom
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          document.getElementById("care-provider-search")?.focus()
        }}
        className="flex w-full max-w-150 min-w-0 flex-col gap-0 p-0">
        <DialogHeader className="h-16 shrink-0 pt-4 pr-4 pb-2 pl-6">
          <DialogTitle className="px-0 py-0" data-testid="care-provider-dialog-title">
            {t("patient.information.addCareProvider")}
          </DialogTitle>
        </DialogHeader>
        <div className="w-full px-6 py-4">
          <Suspense fallback={<p className="h-12" />}>
            <CareProviderSearch
              label={t("common.search")}
              searchType="NONE"
              name="care-provider"
              value={null}
              isDoctorNetwork
              analyticsSectionName="doctor_network"
              onSelect={handleSelect}
              onRemove={() => {}}
            />
          </Suspense>
        </div>
        <DialogFooter className="h-20 shrink-0 items-center" data-testid="care-provider-dialog-footer">
          <Button type="button" variant="secondary" onClick={toggleDialog}>
            {t("common.button.cancel")}
          </Button>
        </DialogFooter>
      </DialogContentCustom>
    </Dialog>
  )
}

export default AddDoctorNetworkDialog
